// src/pages/ARaging.js
// A/R Aging Report — outstanding claims by age bucket with urgent queue

import React, { useState, useEffect, useCallback } from 'react';
import * as awsApi from '../services/awsApiService';
import * as apiService from '../services/apiService';
import {
  Clock, DollarSign, AlertTriangle, RefreshCw,
  TrendingDown, CheckCircle, ChevronRight
} from 'lucide-react';

function formatCurrency(n) {
  if (!n && n !== 0) return '—';
  return '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function urgencyColor(daysOld) {
  if (daysOld >= 90) return { color: '#ef4444', bg: 'rgba(239,68,68,0.1)' };
  if (daysOld >= 60) return { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' };
  return { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)' };
}

const BUCKET_CONFIG = [
  { key: '0-30',  label: '0–30 Days',    color: '#10b981', bg: 'rgba(16,185,129,0.08)', border: '#10b981', note: 'On track' },
  { key: '31-60', label: '31–60 Days',   color: '#3b82f6', bg: 'rgba(59,130,246,0.08)', border: '#3b82f6', note: 'Follow up soon' },
  { key: '61-90', label: '61–90 Days',   color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: '#f59e0b', note: 'Action required' },
  { key: '90+',   label: '90+ Days',     color: '#ef4444', bg: 'rgba(239,68,68,0.08)', border: '#ef4444', note: 'Critical — appeal deadline' },
];

const DEMO_DATA = {
  buckets: [
    { bucket: '0-30',  count: 28, total_amount: 41200, outstanding_amount: 38400 },
    { bucket: '31-60', count: 14, total_amount: 22800, outstanding_amount: 21100 },
    { bucket: '61-90', count: 9,  total_amount: 17300, outstanding_amount: 16200 },
    { bucket: '90+',   count: 5,  total_amount: 9800,  outstanding_amount: 9800  },
  ],
  urgentClaims: [
    { id: 'c1', patient_name: 'Robert Johnson',  payer: 'Cigna',        service_date: '2026-02-12', days_old: 101, claim_amount: 2850, claim_status: 'Submitted', procedure_codes: ['D8080'] },
    { id: 'c2', patient_name: 'James Wilson',    payer: 'MetLife',      service_date: '2026-02-28', days_old: 85,  claim_amount: 1640, claim_status: 'Processing', procedure_codes: ['D2740','D2750'] },
    { id: 'c3', patient_name: 'Sarah Mitchell',  payer: 'Delta Dental', service_date: '2026-03-05', days_old: 80,  claim_amount: 980,  claim_status: 'Submitted', procedure_codes: ['D4341'] },
    { id: 'c4', patient_name: 'Maria Garcia',    payer: 'Aetna',        service_date: '2026-03-10', days_old: 75,  claim_amount: 1200, claim_status: 'Submitted', procedure_codes: ['D7210','D7140'] },
    { id: 'c5', patient_name: 'Emily Chen',      payer: 'Guardian',     service_date: '2026-03-18', days_old: 67,  claim_amount: 560,  claim_status: 'Processing', procedure_codes: ['D0210'] },
  ],
};

export default function ARaging() {
  const [buckets, setBuckets] = useState([]);
  const [urgentClaims, setUrgentClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await awsApi.getClaimAging();
      if (res.success) {
        setBuckets(res.data.buckets || []);
        setUrgentClaims(res.data.urgentClaims || []);
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    if (apiService.getAppMode() === 'demo') {
      setBuckets(DEMO_DATA.buckets);
      setUrgentClaims(DEMO_DATA.urgentClaims);
      setLoading(false);
      return;
    }
    load();
  }, [load]);

  const bucketMap = Object.fromEntries(buckets.map(b => [b.bucket, b]));
  const totalOutstanding = buckets.reduce((sum, b) => sum + parseFloat(b.outstanding_amount || 0), 0);
  const totalClaims = buckets.reduce((sum, b) => sum + parseInt(b.count || 0), 0);
  const criticalAmount = parseFloat(bucketMap['90+']?.outstanding_amount || 0);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 }}>A/R Aging Report</h1>
          <p style={{ color: '#64748b', fontSize: 14, marginTop: 4 }}>
            Outstanding claims by age — prioritize before payers close the appeal window.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => {
              const rows = urgentClaims.map(c => [
                c.patient_name || '', c.payer || '', c.service_date || '',
                c.days_old || '', c.claim_amount || '', c.claim_status || '',
                Array.isArray(c.procedure_codes) ? c.procedure_codes.join(';') : (c.procedure_codes || '')
              ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
              const bucketRows = buckets.map(b => [`"${b.bucket} days"`, `"${b.count}"`, `"${b.outstanding_amount}"`].join(','));
              const csv = [
                'A/R AGING BUCKETS', 'Bucket,Claims,Outstanding',
                ...bucketRows, '',
                'PRIORITY QUEUE (45+ days)', 'Patient,Payer,Service Date,Days Old,Amount,Status,Procedures',
                ...rows
              ].join('\n');
              const a = document.createElement('a');
              a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
              a.download = `ar-aging-${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151' }}
          >
            ↓ Export CSV
          </button>
          {apiService.getAppMode() !== 'demo' && (
            <button
              onClick={load}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', fontSize: 13, cursor: 'pointer', color: '#374151' }}
            >
              <RefreshCw style={{ width: 14, height: 14 }} /> Refresh
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Loading aging report...</div>
      ) : (
        <>
          {/* Summary KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px' }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Total Outstanding</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#0f172a' }}>{formatCurrency(totalOutstanding)}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{totalClaims} open claims</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px' }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Critical (90+ Days)</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: criticalAmount > 0 ? '#ef4444' : '#10b981' }}>{formatCurrency(criticalAmount)}</div>
              <div style={{ fontSize: 12, color: criticalAmount > 0 ? '#ef4444' : '#64748b', marginTop: 4 }}>
                {criticalAmount > 0 ? 'Appeal deadline at risk' : 'No critical claims'}
              </div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px' }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Collection Health</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                {totalOutstanding === 0 ? (
                  <span style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>—</span>
                ) : (
                  <>
                    <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                      {BUCKET_CONFIG.map(cfg => {
                        const b = bucketMap[cfg.key];
                        const pct = totalOutstanding ? (parseFloat(b?.outstanding_amount || 0) / totalOutstanding) * 100 : 0;
                        return <div key={cfg.key} style={{ display: 'inline-block', width: `${pct}%`, height: '100%', background: cfg.color }} />;
                      })}
                    </div>
                  </>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
                {totalOutstanding > 0 ? `${Math.round((criticalAmount / totalOutstanding) * 100)}% in critical bucket` : 'No open claims'}
              </div>
            </div>
          </div>

          {/* Aging Buckets */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 32 }}>
            {BUCKET_CONFIG.map(cfg => {
              const b = bucketMap[cfg.key];
              const count = parseInt(b?.count || 0);
              const outstanding = parseFloat(b?.outstanding_amount || 0);
              const pct = totalOutstanding ? Math.round((outstanding / totalOutstanding) * 100) : 0;
              return (
                <div key={cfg.key} style={{
                  background: cfg.bg, border: `1px solid ${cfg.border}30`,
                  borderTop: `3px solid ${cfg.color}`,
                  borderRadius: 12, padding: '20px 18px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{cfg.label}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: cfg.color, background: `${cfg.color}20`, padding: '2px 8px', borderRadius: 20 }}>
                      {cfg.note}
                    </span>
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: cfg.color, marginBottom: 4 }}>
                    {formatCurrency(outstanding)}
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>
                    {count} claim{count !== 1 ? 's' : ''}
                  </div>
                  <div style={{ height: 4, background: 'rgba(0,0,0,0.07)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: cfg.color, borderRadius: 2 }} />
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{pct}% of A/R</div>
                </div>
              );
            })}
          </div>

          {/* Urgent Claims Table */}
          {urgentClaims.length > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <AlertTriangle style={{ width: 16, height: 16, color: '#f59e0b' }} />
                <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>Priority Queue — 45+ Day Claims</h2>
              </div>
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                      {['Patient', 'Payer', 'Service Date', 'Age', 'Amount', 'Status', 'Codes'].map(h => (
                        <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {urgentClaims.map(claim => {
                      const urg = urgencyColor(claim.days_old);
                      const codes = Array.isArray(claim.procedure_codes)
                        ? claim.procedure_codes
                        : (claim.procedure_codes ? [claim.procedure_codes] : []);
                      return (
                        <tr key={claim.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                          <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{claim.patient_name || '—'}</td>
                          <td style={{ padding: '12px 16px', fontSize: 13, color: '#374151' }}>{claim.payer || '—'}</td>
                          <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(claim.service_date)}</td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, background: urg.bg, color: urg.color, fontSize: 12, fontWeight: 700 }}>
                              <Clock style={{ width: 11, height: 11 }} />
                              {claim.days_old} days
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{formatCurrency(claim.claim_amount)}</td>
                          <td style={{ padding: '12px 16px', fontSize: 12, color: '#64748b' }}>{claim.claim_status}</td>
                          <td style={{ padding: '12px 16px', fontSize: 11, color: '#8b5cf6', fontFamily: 'monospace' }}>
                            {codes.slice(0, 2).join(', ')}{codes.length > 2 ? ` +${codes.length - 2}` : ''}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 12, padding: '12px 16px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <AlertTriangle style={{ width: 15, height: 15, flexShrink: 0, marginTop: 1, color: '#f59e0b' }} />
                <span>Most payers stop accepting appeals after <strong>90 days</strong> from the date of service. Claims in the 61–90 day bucket should be worked <strong>this week</strong>. FlowRCM automation flags these nightly and generates appeals automatically for denied claims.</span>
              </div>
            </div>
          )}

          {urgentClaims.length === 0 && buckets.length === 0 && (
            <div style={{ padding: 60, textAlign: 'center' }}>
              <CheckCircle style={{ width: 40, height: 40, color: '#10b981', margin: '0 auto 12px' }} />
              <p style={{ color: '#64748b', fontWeight: 600 }}>No open claims found</p>
              <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Import claims or submit through the clearinghouse to start tracking A/R aging.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
